import React, { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import FormField from '@cloudscape-design/components/form-field';
import Alert from '@cloudscape-design/components/alert';
import RadioGroup from '@cloudscape-design/components/radio-group';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { ResultViewer } from '../components/ResultViewer';

const QUERIES = [
  { value: 'attached_resources_with_actions', label: 'Resources + Actions', description: 'All resources with allowed actions for a user' },
  { value: 'attached_resources_with_actions_and_users', label: 'Resources + Actions + Users', description: 'Same as above but includes which user granted the access' },
  { value: 'attached_resources_with_roles', label: 'Resources + Roles', description: 'All resources with associated roles for a user' },
  { value: 'attached_flatten_resources_with_roles', label: 'Flatten Resources + Roles', description: 'Same as above but resources flattened to strings' },
  { value: 'attached_user_groups_with_team_user', label: 'User Groups (via Team)', description: 'User groups where user belongs via team membership' },
];

export const RuntimePage: React.FC<{ app: string }> = ({ app }) => {
  const [userType, setUserType] = useState('PERSON');
  const [userValue, setUserValue] = useState('');
  const [query, setQuery] = useState(QUERIES[0].value);
  const { loading, result, execute } = useApi();

  if (!app) return <Alert type="info">Select an application from the top bar.</Alert>;

  const run = () => execute(() =>
    api('GET', `/${encodeURIComponent(app)}/${encodeURIComponent(query)}?user_type=${encodeURIComponent(userType)}&user_value=${encodeURIComponent(userValue)}`)
  );

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Runtime Permission Queries</Header>

      <Container header={<Header variant="h2">Query User Permissions</Header>}>
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="s">
            <FormField label="User Type">
              <Select
                selectedOption={{ value: userType, label: userType }}
                options={['PERSON', 'TEAM', 'SYSTEM', 'EXTERNAL'].map(t => ({ value: t, label: t }))}
                onChange={({ detail }) => setUserType(detail.selectedOption.value!)}
              />
            </FormField>
            <FormField label="User Value">
              <Input value={userValue} onChange={({ detail }) => setUserValue(detail.value)} placeholder="e.g. rabjoshi" />
            </FormField>
          </SpaceBetween>
          <FormField label="Query Type">
            <RadioGroup
              value={query}
              onChange={({ detail }) => setQuery(detail.value)}
              items={QUERIES.map(q => ({
                value: q.value,
                label: q.label,
                description: q.description,
              }))}
            />
          </FormField>
          <Button variant="primary" onClick={run} loading={loading} disabled={!userValue}>
            Run Query
          </Button>
        </SpaceBetween>
      </Container>

      <ResultViewer data={result?.data} error={result?.error} />
    </SpaceBetween>
  );
};
